<!--
  Created 14.10.2023
  Media component for displaying a single image or video, with caption and alt texts.
  Supports blur-hash, overlay and custom image classes.
-->
<template>
  <figure
    class="group aspect-[16/10] size-full max-h-screen"
    :class="[{'flex flex-col':!figCaptionOutside}, {'max-sm:h-[60vh]':hasLargeHeightMobile}]">
    <!-- Video if of type -->
    <video
      v-if="image?.mime_type?.includes('video/')"
      playsinline
      autoplay
      muted
      loop
      class="size-full object-cover"
      :class="imageClasses"
      :src="image?.original_url"
      :type="image?.mime_type">
      Your browser does not support the video tag.
    </video>
    <!-- Else image if exists -->
    <div
      v-else-if="image !== null && ((typeof image === 'string' && image.length > 1) || (typeof image === 'object' && image?.id))"
      class="relative size-full">
      <!-- Blurred version of the image for preloading -->
      <canvas
        v-if="image?.custom_properties?.blur_hash"
        ref="canvas"
        class="absolute inset-0 size-full"
        :class="imageClasses"
        width="32"
        height="32" />
      <!-- Display image if of supported types -->
      <img
        v-if="image?.mime_type?.includes('image/') || typeof image?.id === 'string' || typeof image === 'string'"
        class="relative size-full object-cover"
        :class="imageClasses"
        :src="image?.mime_type?.includes('image/') ? (image?.large ?? image?.original_url) : (typeof image?.id === 'string' ? image?.urls?.regular : image)"
        :srcset="image?.srcset"
        :alt="imageAlt">
      <!-- Handle unknown media type -->
      <div
        v-else
        class="pointer-events-none flex size-full flex-col items-center justify-center bg-indigo-100 object-cover text-indigo-700 group-hover:opacity-75">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="size-12">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        <span>Unknown media type</span>
      </div>
      <!-- Gradient top/bottom black overlay, if activated -->
      <div
        v-if="imageOverlay"
        class="blur-1xl absolute inset-0 size-full transform-gpu overflow-hidden object-cover"
        :class="imageClasses"
        aria-hidden="true">
        <div class="relative size-full bg-gradient-to-b from-black/25 via-black/0 to-black/25" />
      </div>
    </div>
    <!-- If no image selected, let user know -->
    <img
      v-else
      class="size-full bg-gray-300 object-cover pl-4 pt-4 text-sm italic text-gray-400"
      :class="imageClasses"
      src=""
      alt="Image not selected">
    <!-- Image description, if presented -->
    <figcaption
      v-if="figCaption && !hideFigCaption"
      :class="[
        'mt-1 flex flex-col text-center text-base text-gray-500',
        {'absolute bottom-1 w-full font-medium text-transparent group-hover:text-white group-hover:text-shadow-sm': !figCaptionOutside}
      ]">
      <span
        v-if="image?.custom_properties?.credit"
        :class="[
          'block text-xs italic text-gray-400',
          {'order-2 text-transparent group-hover:text-white': !figCaptionOutside}
        ]"
        :style="{ order: figCaptionOutside ? 2 : 1 }">
        Credit: {{ image.custom_properties?.credit }}
      </span>
      <span
        v-if="!image?.file_name?.startsWith(figCaption)"
        :style="{ order: figCaptionOutside ? 1 : 2 }">
        {{ figCaption }}
      </span>
    </figcaption>
  </figure>
</template>
<script setup>
/* external */
import {
  onMounted, ref,
} from 'vue';
import { decode } from 'blurhash';
/* local */

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */
const props = defineProps({
  image: {
    type: [String, Object],
    required: true,
    default: '',
  },
  imageAlt: {
    type: String,
    required: false,
    default: '',
  },
  hideFigCaption: {
    type: Boolean,
    required: false,
    default: false,
  },
  figCaption: {
    type: String,
    required: false,
    default: '',
  },
  figCaptionOutside: {
    type: Boolean,
    required: false,
    default: false,
  },
  imageClasses: {
    type: [String, Object, Array],
    required: false,
    default: '',
  },
  imageOverlay: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasLargeHeightMobile: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const canvas = ref(null);

/* *********************************************************************
 * Page lifecycle functions
 * ****************************************************************** */

onMounted(() => {
  if (props.image?.custom_properties?.blur_hash) {
    const pixels = decode(props.image.custom_properties?.blur_hash, 32, 32);
    const imageData = new ImageData(pixels, 32, 32);
    const context = canvas.value.getContext('2d');
    context.putImageData(imageData, 0, 0);
  }
});
</script>
